import { useEffect, useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  DEFAULT_INCOMEIQUEUE_RCM,
  INCOMEIQUEUE_RCM_NETWORK_TYPES,
  hydrateNetworkScenarios
} from 'lib/constants/healthcareProgram/incomeIQueueRcm';
import { useHealthcareProgramConfig } from 'lib/hooks/queries/healthcareProgram/useHealthcareProgramConfig';
import { useUpdateHealthcareProgramConfig } from 'lib/hooks/queries/healthcareProgram/useUpdateHealthcareProgramConfig';

export const useIncomeIQueueDraft = ({ setIsDirty, practiceId, enabled = true } = {}) => {
  const { data, isLoading, isError } = useHealthcareProgramConfig({
    ...(practiceId != null && { practiceId }),
    enabled
  });
  const persistedSetup = data?.incomeiqueue_rcm ?? DEFAULT_INCOMEIQUEUE_RCM;

  const [draftState, setDraftState] = useState(null);
  const [activeNetwork, setActiveNetwork] = useState(INCOMEIQUEUE_RCM_NETWORK_TYPES.IN_NETWORK);

  const draft = draftState ?? persistedSetup;
  const isDirty = useMemo(() => !isEqual(draft, persistedSetup), [draft, persistedSetup]);

  useEffect(() => {
    setIsDirty?.(isDirty);
    return () => setIsDirty?.(false);
  }, [isDirty, setIsDirty]);

  const {
    mutate: saveConfig,
    mutateAsync: saveConfigAsync,
    isLoading: isSaving
  } = useUpdateHealthcareProgramConfig({ practiceId });

  const activeNetworkValues = useMemo(
    () => hydrateNetworkScenarios(draft, activeNetwork),
    [draft, activeNetwork]
  );

  const handleNetworkChange = (item) => setActiveNetwork(item.value);

  const handleScenariosChange = (nextValues) => {
    setDraftState((prev) => ({ ...(prev ?? persistedSetup), [activeNetwork]: nextValues }));
  };

  const reset = () => setDraftState(null);

  const save = () => saveConfig({ incomeiqueue_rcm: draft });

  const saveAsync = async () => {
    const res = await saveConfigAsync({ incomeiqueue_rcm: draft });
    return res?.code === 0;
  };

  return {
    data,
    isLoading,
    isError,
    draft,
    isDirty,
    isSaving,
    activeNetwork,
    setActiveNetwork,
    activeNetworkValues,
    handleNetworkChange,
    handleScenariosChange,
    reset,
    save,
    saveAsync
  };
};
